import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, Group, Player } from "../api/client";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";

type Attendance = { attended: number; total: number };
type StatusFilter = "active" | "inactive" | "all";
type EditForm = {
  name: string;
  login: string;
  password: string;
  birthDate: string;
  groupID: string;
  certExpiry: string;
};

// "2014-03-02" → "02.03.2014", from the string: new Date() would shift the day
// west of Greenwich.
function formatDate(value: string | null): string {
  if (!value) return "—";
  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

function formatAttendance(a: Attendance | undefined): string {
  if (!a || a.total === 0) return "—";
  return `${a.attended} из ${a.total} (${Math.round((a.attended / a.total) * 100)}%)`;
}

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "ru");

export default function PlayersPage() {
  const [players, setPlayers] = useState<Player[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [attendance, setAttendance] = useState<Record<string, Attendance>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<StatusFilter>("active");
  const [groupFilter, setGroupFilter] = useState("");
  const [query, setQuery] = useState("");
  const [editingID, setEditingID] = useState<string | null>(null);
  const [form, setForm] = useState<EditForm | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const [playerList, groupList, stats] = await Promise.all([
        api.listPlayers(),
        api.listGroups(),
        // Attendance only fills a column; the list must still show without it.
        api.listPlayerAttendance().catch((err) => {
          console.error("Failed to load player attendance", err);
          return null;
        }),
      ]);
      setPlayers((playerList ?? []).slice().sort(byName));
      setGroups((groupList ?? []).slice().sort(byName));
      const byPlayer: Record<string, Attendance> = {};
      for (const s of stats ?? []) {
        byPlayer[s.player_id] = { attended: Number(s.attended), total: Number(s.total_sessions) };
      }
      setAttendance(byPlayer);
    } catch (err: any) {
      console.error("Failed to load players", err);
      setError(err?.message || "Не удалось загрузить детей");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groupName = new Map(groups.map((g) => [g.id, g.name]));
  const editing = editingID === null ? null : players.find((p) => p.id === editingID) ?? null;

  const q = query.trim().toLowerCase();
  const visible = players.filter(
    (p) =>
      (status === "all" || (status === "active") === p.is_active) &&
      (groupFilter === "" || (groupFilter === "none" ? !p.group_id : p.group_id === groupFilter)) &&
      (q === "" || p.name.toLowerCase().includes(q) || p.login.toLowerCase().includes(q))
  );

  const run = async (action: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err: any) {
      console.error("Player action failed", err);
      setError(err?.message || failure);
    } finally {
      setBusy(false);
      await load();
    }
  };

  const startEditing = (p: Player) => {
    setError(null);
    setEditingID(p.id);
    setForm({
      name: p.name,
      login: p.login,
      password: "",
      birthDate: p.birth_date?.slice(0, 10) ?? "",
      groupID: p.group_id ?? "",
      certExpiry: p.medical_cert_expiry?.slice(0, 10) ?? "",
    });
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const stopEditing = () => {
    setEditingID(null);
    setForm(null);
  };

  const set = (changes: Partial<EditForm>) => setForm((f) => (f ? { ...f, ...changes } : f));

  const canSave = !busy && form !== null && form.name.trim() !== "" && form.login.trim() !== "";

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing || !form || !canSave) return;
    const password = form.password.trim();
    setBusy(true);
    setError(null);
    try {
      await api.updatePlayer(editing.id, {
        name: form.name.trim(),
        login: form.login.trim(),
        birth_date: form.birthDate || null,
        group_id: form.groupID || null,
        medical_cert_expiry: form.certExpiry || null,
        ...(password ? { password } : {}),
      });
      stopEditing();
      await load();
    } catch (err: any) {
      console.error("Player save failed", err);
      setError(err?.message || "Не удалось сохранить ребёнка");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (p: Player) => {
    const confirmed = await confirm({
      title: `Деактивировать «${p.name}»?`,
      body: (
        <>
          <p>
            Ребёнок не сможет войти в приложение и выйдет из командного чата группы. Неоплаченные
            счета за него отменятся. Его родители выйдут из родительского чата группы, если других
            детей в ней у них нет.
          </p>
          <p>Данные и история сохранятся — восстановить ребёнка можно в списке неактивных.</p>
        </>
      ),
      confirmLabel: "Деактивировать",
      danger: true,
    });
    if (!confirmed) return;
    if (editingID === p.id) stopEditing();
    await run(() => api.deactivatePlayer(p.id), "Не удалось деактивировать ребёнка");
  };

  const restore = (p: Player) => run(() => api.restorePlayer(p.id), "Не удалось восстановить ребёнка");

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide">
      <h2>Дети</h2>
      <p className="page-description">
        Все дети клуба. Деактивированный ребёнок не может войти в приложение, но его данные
        сохраняются, и его можно восстановить. При переводе в другую группу сервер сам переносит
        ребёнка в её командный чат.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      {editing && form && (
        <form ref={formRef} className="mav-card cp-form player-edit-form" aria-label="Изменение ребёнка" onSubmit={save}>
          <h3 className="coach-form-title">Изменение: {editing.name}</h3>
          <label className="cp-field">
            <span>Имя</span>
            <input className="mav-input" value={form.name} maxLength={100} disabled={busy} onChange={(e) => set({ name: e.target.value })} />
          </label>
          <label className="cp-field">
            <span>Логин</span>
            <input className="mav-input" value={form.login} maxLength={100} autoComplete="off" disabled={busy} onChange={(e) => set({ login: e.target.value })} />
          </label>
          <div className="cp-field">
            <label className="cp-field">
              <span>Новый пароль</span>
              <input className="mav-input" value={form.password} maxLength={100} autoComplete="off" disabled={busy} onChange={(e) => set({ password: e.target.value })} />
            </label>
            <span className="mav-hint">Оставьте пустым, чтобы не менять</span>
          </div>
          <label className="cp-field">
            <span>Дата рождения</span>
            <input className="mav-input" type="date" value={form.birthDate} disabled={busy} onChange={(e) => set({ birthDate: e.target.value })} />
          </label>
          <label className="cp-field">
            <span>Группа ребёнка</span>
            <select className="mav-input" value={form.groupID} disabled={busy} onChange={(e) => set({ groupID: e.target.value })}>
              <option value="">Без группы</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          <label className="cp-field">
            <span>Справка действует до</span>
            <input className="mav-input" type="date" value={form.certExpiry} disabled={busy} onChange={(e) => set({ certExpiry: e.target.value })} />
          </label>
          <div className="cp-actions mav-actions">
            <button type="submit" className="mav-btn-primary" disabled={!canSave}>
              Сохранить
            </button>
            <button type="button" className="mav-btn-secondary" disabled={busy} onClick={stopEditing}>
              Отмена
            </button>
          </div>
        </form>
      )}

      <div className="players-filters">
        <label className="cp-field">
          <span>Группа</span>
          <select className="mav-input" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
            <option value="">Все группы</option>
            <option value="none">Без группы</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label className="cp-field">
          <span>Статус</span>
          <select className="mav-input" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
            <option value="active">Активные</option>
            <option value="inactive">Неактивные</option>
            <option value="all">Все</option>
          </select>
        </label>
        <label className="cp-field players-search">
          <span>Поиск</span>
          <input className="mav-input" type="search" placeholder="Имя или логин" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <Link className="mav-btn-primary players-add" to="/players/new">
          Добавить ребёнка
        </Link>
      </div>
      <p className="players-count">
        Показано {visible.length} из {players.length}
      </p>

      <div className="table-wrap">
        <table className="settings-table stack-table players-table">
          <thead>
            <tr>
              <th>Ребёнок</th>
              <th>Группа</th>
              <th>Даты</th>
              <th>Посещаемость</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-cell">
                  {error ? "—" : players.length === 0 ? "Детей пока нет" : "Никого не нашлось"}
                </td>
              </tr>
            ) : (
              visible.map((p) => (
                <tr key={p.id}>
                  <td className="cell-player-name">
                    <span className="player-name">{p.name}</span>
                    <span className="player-login">{p.login}</span>
                    {!p.is_active && <span className="mav-badge mav-badge-muted">Неактивен</span>}
                  </td>
                  <td className="cell-player-group">
                    {p.group_id ? (
                      groupName.get(p.group_id) ?? "—"
                    ) : (
                      <span className="mav-status-muted">Без группы</span>
                    )}
                  </td>
                  <td className="date-cell cell-player-dates">
                    <span>{formatDate(p.birth_date)}</span>
                    <span className="player-cert">
                      {p.medical_cert_expiry ? `справка до ${formatDate(p.medical_cert_expiry)}` : "справки нет"}
                    </span>
                  </td>
                  <td className="cell-player-attendance">{formatAttendance(attendance[p.id])}</td>
                  <td className="cell-action">
                    <div className="row-actions">
                      <button className="mav-btn-secondary" disabled={busy} onClick={() => startEditing(p)}>
                        Изменить
                      </button>
                      {p.is_active ? (
                        <button className="btn-quiet-danger" disabled={busy} onClick={() => deactivate(p)}>
                          Деактивировать
                        </button>
                      ) : (
                        <button className="mav-btn-secondary" disabled={busy} onClick={() => restore(p)}>
                          Восстановить
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {dialog}
    </div>
  );
}
